import { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { formatDate } from '@/lib/format';
import { History, RotateCcw } from 'lucide-react';
import { motion } from 'framer-motion';

interface HistoryEntry {
  id: string;
  product_type: string;
  month_index: number;
  expected_recovery_rate: number;
  changed_at: string;
  comment: string;
}

interface CurveHistoryDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productType: string;
}

export const CurveHistoryDrawer = ({ open, onOpenChange, productType }: CurveHistoryDrawerProps) => {
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && productType) {
      loadHistory();
    }
  }, [open, productType]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('product_curve_versions' as any)
        .select('*')
        .eq('product_type', productType)
        .order('changed_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setHistory((data as any[]) || []);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group by changed_at timestamp
  const groupedHistory = history.reduce((acc, entry) => {
    const key = entry.changed_at;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(entry);
    return acc;
  }, {} as Record<string, HistoryEntry[]>);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Version History
          </SheetTitle>
          <SheetDescription>
            Changes to {productType} recovery curve
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-120px)] mt-6">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading history...
            </div>
          ) : Object.keys(groupedHistory).length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No version history yet
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedHistory).map(([timestamp, entries], index) => {
                const avgRate = entries.reduce((sum, e) => sum + parseFloat(String(e.expected_recovery_rate)), 0) / entries.length;
                
                return (
                  <motion.div
                    key={timestamp}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="border rounded-lg p-4 space-y-2 hover:border-primary transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-medium">{formatDate(timestamp)}</p>
                        <p className="text-xs text-muted-foreground">
                          {entries.length} month{entries.length > 1 ? 's' : ''} updated
                        </p>
                      </div>
                      <Badge variant="secondary">
                        Avg: {(avgRate * 100).toFixed(2)}%
                      </Badge>
                    </div>

                    <div className="flex flex-wrap gap-1">
                      {entries.slice(0, 5).map(entry => (
                        <Badge key={entry.id} variant="outline" className="text-xs">
                          M{entry.month_index}: {(parseFloat(String(entry.expected_recovery_rate)) * 100).toFixed(2)}%
                        </Badge>
                      ))}
                      {entries.length > 5 && (
                        <Badge variant="outline" className="text-xs">
                          +{entries.length - 5} more
                        </Badge>
                      )}
                    </div>

                    {entries[0]?.comment && (
                      <p className="text-xs text-muted-foreground italic">
                        {entries[0].comment}
                      </p>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
